import {
  caShareCellStyle,
  PLANNING_HOUR_END,
  PLANNING_HOUR_START,
  type StorePlanningClockStatus,
  type StorePlanningCoverageKind,
  storeProcessTaskTheme,
} from '@gozoki/api-types'

// Géométrie de la grille, en px du canvas logique 1920 × 1080 (cotes Figma 10715:56059 lues 1:1).
// La maquette dessine une heure sur 48 px, soit 776 px de grille : c'est plus que la hauteur que
// la colonne centrale du dashboard peut offrir une fois le widget Flux supply posé au-dessus. On
// garde ces cotes réelles et le widget fait défiler verticalement (cf. `PlanningWeekCard`).
export const ROW_HEIGHT = 48
export const GRID_TOP = 8
export const ROW_COUNT = PLANNING_HOUR_END - PLANNING_HOUR_START
export const GRID_HEIGHT = GRID_TOP + ROW_COUNT * ROW_HEIGHT

// Ligne de titres d'un jour (pastille du jour) + son écart de 8 px avec la grille : hauteur du
// bandeau d'en-tête épinglé pendant le défilement.
export const DAY_HEADER_HEIGHT = 33

// Colonnes internes d'un jour : affluence | couverture | timeline, séparées de 4 px.
export const AFFLUENCE_WIDTH = 18
export const COVERAGE_WIDTH = 6

// Carte collaborateur : l'avatar et le badge de pointage occupent une hauteur fixe au-dessus de
// l'heure de début, et le libellé de fin une hauteur fixe en dessous de l'heure de fin.
export const SHIFT_CARD_WIDTH = 40
export const SHIFT_CARD_HEAD = 64
export const SHIFT_CARD_FOOT = 16
// Espaces minimaux entre deux cartes : côte à côte (entre lanes) et l'une sous l'autre (même lane).
export const SHIFT_CARD_LANE_GAP = 2
export const SHIFT_CARD_STACK_GAP = 4
// Bande gauche du jour jamais couverte par les cartes : ce qu'il restait des tâches et livraisons
// avec 3 lanes dans l'ancienne mise en page (140 px de timeline - 108 px de cartes).
export const TASK_VISIBLE_WIDTH = 32

export const DAY_LABELS = ['LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM', 'DIM'] as const

// Colonne « ca » : charte de la colonne CA du widget « Couverture planning » du dashboard RH,
// source unique `caShareCellStyle` (@gozoki/api-types) : 8 paliers à seuils ABSOLUS de part de CA
// (pas relatifs au max de la semaine). `value` est la part de l'heure en % entier 0..100, convertie
// en fraction : pour un entier, `value / 100` vaut exactement le littéral `0.0x` des seuils, les
// bords restent donc inclusifs (3 pile -> palier 3).
export const affluenceCellStyle = (value: number) => caShareCellStyle(value / 100)

export const COVERAGE_COLOR: Record<StorePlanningCoverageKind, string> = {
  presentiel: '#79627B',
  distanciel: '#A38FA5',
  ouverture: '#C7B4C8',
}

export const COVERAGE_LABEL: Record<StorePlanningCoverageKind, string> = {
  presentiel: 'Présentiel',
  distanciel: 'Distanciel',
  ouverture: 'Ouvertures',
}

// Bloc « tâche process » dans la grille : palette partagée avec le back-office
// (`storeProcessTaskTheme`, source de vérité dans @gozoki/api-types). `bar` = liseré gauche,
// `bg` = fond du bloc, `text` = icône + heure.
export const TASK_THEME = storeProcessTaskTheme

export const CLOCK_COLOR: Record<StorePlanningClockStatus, string> = {
  on_time: '#2A9D5C',
  late: '#F0A945',
  missing: '#F04545',
  // Neutre : un créneau à venir n'est ni conforme ni en anomalie.
  upcoming: '#8A8676',
}

export const CLOCK_LABEL: Record<StorePlanningClockStatus, string> = {
  on_time: 'Pointé',
  late: 'Pointé en retard — à valider',
  missing: 'Non pointé',
  upcoming: 'À venir',
}

export const minutesOf = (time: string): number => {
  const [hours, minutes] = time.split(':')
  return Number(hours) * 60 + Number(minutes ?? 0)
}

// Ordonnée d'un instant dans la grille, bornée à la plage affichée.
export const yOf = (minutes: number, rowHeight: number = ROW_HEIGHT): number => {
  const offset = (minutes - PLANNING_HOUR_START * 60) / 60
  return GRID_TOP + Math.max(0, Math.min(ROW_COUNT, offset)) * rowHeight
}

export const HOUR_LABELS = Array.from(
  { length: ROW_COUNT + 1 },
  (_, index) => `${String(PLANNING_HOUR_START + index).padStart(2, '0')}:00`,
)
